import React from 'react';
import { CenteredContentLayout } from 'components/containers/CenteredContentLayout';
import { PanelHeader } from 'components/containers/Panel/PanelHeader.view';
import { SettingsType } from '../../../hooks/useSettings';
import { Panel } from 'components/containers/Panel';
import { ConnectProfile } from 'components/socialProfiles/ConnectProfile';
import {
  CustomBitlySettingsValuesInput,
  SocialOAuthProvider,
  UpdateCustomBitlySettingsInput,
  UtmInput
} from '../../../models/graphql';
import {
  GetBitlyAccounts_workspace_bitlyAccounts,
  GetBitlyAccounts_workspace_socialProfiles
} from '../../../models/legacy-graphql';
import { ButtonColorsEnum } from '../../../constants/button-colors';
import { EditUtmModal } from './EditUtmModal';
import { ErrorModalType, UTMSettingsType } from './LinkTracking.controller';
import { ToggleAction } from 'components/forms/Toggle/Toggle.controller';
import { SelectItem } from 'components/forms/Select/Select.view';
import { MappedProfileType } from 'components/socialProfiles/SocialProfileListItem/SocialProfileListItem.view';
import { Modal } from 'components/containers/Modal';
import { LinkTrackingPreferencesPanel } from './LinkTrackingPreferencesPanel';
import { BitlyAccountPanel } from './BitlyAccountPanel';
import { LinkTrackingPreferencesPanelViewProps } from './LinkTrackingPreferencesPanel/LinkTrackingPreferencesPanel.view';
import { GoogleAnalyticsPanel } from './GoogleAnalyticsPanel';
import { GoogleAnalyticsPanelViewProps } from './GoogleAnalyticsPanel/GoogleAnalyticsPanel.view';
import { FormElementAction } from '../../../models/basic-objects';

export type LinkTrackingViewProps = {
  settings: SettingsType;
  connectedBitlyAccounts: GetBitlyAccounts_workspace_bitlyAccounts[];
  disconnectedBitlyAccounts: GetBitlyAccounts_workspace_bitlyAccounts[];
  errorModal: ErrorModalType | null;
  setErrorModal: (error: ErrorModalType | null) => void;
  showDeleteConfirmation: boolean;
  setShowDeleteConfirmation: (show: boolean) => void;
  bitlyAccountToDeleteId: string;
  showCustomLinkTracking: boolean;
  setShowCustomLinkTracking: (show: boolean) => void;
  showUtmEditModal: boolean;
  setShowUtmEditModal: (show: boolean) => void;
  utmSettings: UTMSettingsType;
  defaultBitlyDomainSelectHandler: FormElementAction;
  customBitlyDomainSelectHandler: FormElementAction;
  customUtmSettingsToggleHandler: ToggleAction;
  removeUTMParametersToggleHandler: ToggleAction;
  customLinkTrackingToggleHandler: ToggleAction;
  socialProfiles: GetBitlyAccounts_workspace_socialProfiles[];
  bitlyConnectButtonHandler: (isReconnect?: boolean, profile?: MappedProfileType) => void;
  removeBitlyAccountHandler: (bitlyId: string, confirmed?: boolean) => void;
  loading: boolean;
  customizePerProfileLoading: boolean;
  bitlyAccounts: GetBitlyAccounts_workspace_bitlyAccounts[];
  getDefaultBitlyDomainListSelectItems: () => Array<SelectItem>;
  getBitlyDomainListSelectItems: (profileId: string) => Array<SelectItem>;
  getDefaultBitlyInputValue: () => CustomBitlySettingsValuesInput;
  getCustomBitlySettingsValueInput: (profileId: string) => UpdateCustomBitlySettingsInput;
};

export const LinkTrackingView = (props: LinkTrackingViewProps): JSX.Element => {
  const campaign: UtmInput = {
    source: props.utmSettings.utmSource,
    medium: props.utmSettings.utmMedium,
    campaign: props.utmSettings.utmCampaign,
    term: props.utmSettings.utmTerm,
    content: props.utmSettings.utmContent
  };

  const preferencesPanelPropValues: LinkTrackingPreferencesPanelViewProps = {
    loading: props.loading || props.customizePerProfileLoading,
    socialProfiles: props.socialProfiles,
    getDefaultBitlyDomainListSelectItems: props.getDefaultBitlyDomainListSelectItems,
    getBitlyDomainListSelectItems: props.getBitlyDomainListSelectItems,
    getDefaultBitlyInputValue: props.getDefaultBitlyInputValue,
    getCustomBitlySettingsValueInput: props.getCustomBitlySettingsValueInput,
    showCustomLinkTracking: props.showCustomLinkTracking,
    setShowCustomLinkTracking: props.setShowCustomLinkTracking,
    customBitlyDomainSelectHandler: props.customBitlyDomainSelectHandler,
    customLinkTrackingToggleHandler: props.customLinkTrackingToggleHandler,
    defaultBitlyDomainSelectHandler: props.defaultBitlyDomainSelectHandler
  };

  const googleAnalyticsPanelPropValues: GoogleAnalyticsPanelViewProps = {
    utmSettings: props.utmSettings,
    setShowUtmEditModal: props.setShowUtmEditModal,
    removeUTMParametersToggleHandler: props.removeUTMParametersToggleHandler,
    customUtmSettingsToggleHandler: props.customUtmSettingsToggleHandler
  };

  return (
    <>
      <Panel>
        <PanelHeader title={'Connect Bitly Account'} subTitle={'You can add multiple Bitly accounts'} />
        <div className={'grid-x grid-margin-x margin-bottom-small-16'}>
          <div className={'cell shrink margin-bottom-small-16'}>
            <ConnectProfile
              oAuthProvider={SocialOAuthProvider.BITLY}
              action={() => props.bitlyConnectButtonHandler(false)}
            />
          </div>
        </div>
      </Panel>
      {props.disconnectedBitlyAccounts.length > 0 && (
        <BitlyAccountPanel
          bitlyAction={props.bitlyConnectButtonHandler}
          areAccountsConnected={false}
          bitlyList={props.disconnectedBitlyAccounts}
          removeBitlyAccountHandler={props.removeBitlyAccountHandler}
        />
      )}
      {props.connectedBitlyAccounts.length === 0 ? (
        <Panel>
          <PanelHeader title={'Connected Bitly Accounts'} />
          <div className={'grid-x grid-margin-x margin-bottom-small-16'}>
            <div className={'cell shrink margin-bottom-small-16'}>
              <div>You do not have any Bitly accounts connected</div>
            </div>
          </div>
        </Panel>
      ) : (
        <>
          <BitlyAccountPanel
            bitlyAction={props.bitlyConnectButtonHandler}
            areAccountsConnected={true}
            bitlyList={props.connectedBitlyAccounts}
            removeBitlyAccountHandler={props.removeBitlyAccountHandler}
          />
          <LinkTrackingPreferencesPanel {...preferencesPanelPropValues} />
        </>
      )}
      <GoogleAnalyticsPanel {...googleAnalyticsPanelPropValues} />
      {props.showUtmEditModal && <EditUtmModal onClose={() => props.setShowUtmEditModal(false)} campaign={campaign} />}
      {props.errorModal && (
        <Modal
          title={props.errorModal.title}
          onClose={() => props.setErrorModal(null)}
          showCloseIcon={true}
          primaryButtonColor={ButtonColorsEnum.secondary}
          primaryButtonAction={props.errorModal.action}
          primaryButtonTitle={'Try again'}
          primaryButtonLoading={false}>
          {props.errorModal.content}
        </Modal>
      )}
      {props.showDeleteConfirmation && (
        <Modal
          title={'Remove Bitly account'}
          onClose={() => props.setShowDeleteConfirmation(false)}
          showCloseIcon={true}
          primaryButtonColor={ButtonColorsEnum.alert}
          primaryButtonAction={() => props.removeBitlyAccountHandler(props.bitlyAccountToDeleteId, true)}
          primaryButtonTitle={'Remove'}
          primaryButtonLoading={false}>
          <div>Are you sure you want to remove this Bitly account?</div>
        </Modal>
      )}
    </>
  );
};

export const LinkTrackingViewLayout = (props: LinkTrackingViewProps): JSX.Element => {
  return (
    <CenteredContentLayout headerTitle={'Link Tracking'} loading={props.loading} hasSidebar>
      <LinkTrackingView {...props} />
    </CenteredContentLayout>
  );
};
